
import { defineComponent } from 'vue';
import { setActivePinia } from 'pinia';
import { pinia } from '@/stores';
import { useCheckoutStore } from '@/stores/useCheckoutStore';
import CustomerHelper from '@/helpers/customerHelper';
import { joinClass } from '@/services/classJoinService';
import CartService from '@/services/cartService';
import router from '@/router';

function getCheckoutStore() {
  setActivePinia(pinia);
  return useCheckoutStore();
}
import { normalizeBaseUrl } from '@/helpers/tenantUrlHelper';
import { DateHelper } from '@/helpers/dateHelper';
import AppButton from '@/components/buttons/AppButton.vue';
import ErrorModal from '@/components/popup/modal/errorModal.vue';

export default defineComponent({
  name: 'ClassJoinButton',
  inheritAttrs: false,
  components: {
    AppButton,
    ErrorModal
  },
  props: {
    classData: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      default: 'Apuntarse ahora'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'primary'
    },
    block: {
      type: Boolean,
      default: false
    },
    selectedSlot: {
      type: Object,
      default: null
    },
    showErrorModal: {
      type: Boolean,
      default: true
    },
    errorModalTitle: {
      type: String,
      default: null
    },
    errorModalCtaLabel: {
      type: String,
      default: null
    },
    errorModalFallbackMessage: {
      type: String,
      default: null
    }
  },
  emits: ['success', 'error'],
  data() {
    return {
      loading: false,
      errorModalVisible: false,
      errorModalMessages: []
    };
  },
  computed: {
    resolvedErrorTitle() {
      if (typeof this.errorModalTitle === 'string' && this.errorModalTitle.trim().length) {
        return this.errorModalTitle;
      }
      return this.$t
        ? this.$t("We couldn't complete your booking")
        : "We couldn't complete your booking";
    },
    resolvedErrorCtaLabel() {
      if (typeof this.errorModalCtaLabel === 'string' && this.errorModalCtaLabel.trim().length) {
        return this.errorModalCtaLabel;
      }
      return this.$t
        ? this.$t('Select another time slot')
        : 'Select another time slot';
    },
    resolvedFallbackMessage() {
      if (typeof this.errorModalFallbackMessage === 'string' && this.errorModalFallbackMessage.trim().length) {
        return this.errorModalFallbackMessage;
      }
      return this.$t
        ? this.$t('The selected time slot is no longer available. Please choose another time.')
        : 'The selected time slot is no longer available. Please choose another time.';
    }
  },
  methods: {
    extractMessagesFromResponse(responseData) {
      if (!responseData) {
        return [];
      }

      const messages = [];

      if (typeof responseData === 'string') {
        messages.push(responseData);
      } else if (typeof responseData === 'object') {
        if (Array.isArray(responseData.messages)) {
          responseData.messages.forEach((item) => {
            if (item) {
              messages.push(String(item));
            }
          });
        }

        if (responseData.model && typeof responseData.model === 'object') {
          Object.values(responseData.model).forEach((value) => {
            if (value) {
              messages.push(String(value));
            }
          });
        }

        if (responseData.errorMsg) {
          messages.unshift(String(responseData.errorMsg));
        }

        if (responseData.message) {
          messages.unshift(String(responseData.message));
        }
      }

      return messages;
    },
    buildErrorPayload(error) {
      const fallbackMessage = this.$t ? this.$t("We couldn't complete your booking") : 'Unable to complete booking';
      const payload = {
        message: fallbackMessage,
        details: [],
        raw: error,
      };

      const responseData = (error && typeof error === 'object' && error.response && 'data' in error.response)
        ? error.response.data
        : null;

      if (responseData) {
        const extracted = this.extractMessagesFromResponse(responseData);
        const filtered = extracted
          .map((msg) => (typeof msg === 'string' ? msg.trim() : ''))
          .filter((msg) => Boolean(msg));

        if (filtered.length === 0 && error.message) {
          filtered.push(error.message);
        }

        payload.message = filtered[0] || fallbackMessage;
        payload.details = filtered;
        return payload;
      }

      if (error && typeof error === 'object' && error.message) {
        payload.message = error.message;
      }

      return payload;
    },
    normalizeAmount(value) {
      if (value === null || value === undefined) {
        return null;
      }
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return null;
      }
      return Math.round(numeric * 100) / 100;
    },
    buildPriceLabel(amount) {
      const normalized = this.normalizeAmount(amount);
      if (normalized === null) {
        return null;
      }
      const locale = this.classData?.price?.locale || navigator.language || 'es-ES';
      const currency = this.classData?.price?.currencyCode || 'EUR';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
        }).format(normalized);
      } catch (_) {
        return `${normalized.toFixed(2)} ${currency}`;
      }
    },
    async handleClick() {
      if (this.disabled || this.loading) {
        return;
      }
      const isLogged = CustomerHelper.isUserLogged ? CustomerHelper.isUserLogged() : Boolean(CustomerHelper.getUser());
      if (!isLogged) {
        CustomerHelper.loginIfNotLogged?.(false);
        return;
      }

      this.loading = true;
      try {
        const result = await joinClass(this.classData, { slot: this.selectedSlot });

        if (!result?.ok) {
          const messages = this.extractMessagesFromResponse(result);
          const sanitized = messages
            .map((msg) => (typeof msg === 'string' ? msg.trim() : ''))
            .filter((msg) => msg.length > 0);

          const fallbackMessage = this.$t
            ? this.$t('The selected time slot is no longer available. Please choose another time.')
            : 'The selected time slot is no longer available. Please choose another time.';

          const errorPayload = {
            message: sanitized[0] || fallbackMessage,
            details: sanitized.length ? sanitized : [fallbackMessage],
            raw: result
          };

          getCheckoutStore().setCheckoutContext(null);
          this.openErrorModal(errorPayload.details.length ? errorPayload.details : [errorPayload.message]);
          this.$emit('error', errorPayload);
          return;
        }

        const slot = this.selectedSlot ?? (Array.isArray(this.classData.bookings) ? this.classData.bookings[0] : null) ?? {};
        let durationMinutes = typeof slot.duration === 'number' && slot.duration > 0
          ? Math.round(slot.duration)
          : null;
        if ((durationMinutes === null || durationMinutes === 0) && slot.start && slot.end) {
          const start = new Date(slot.start);
          const end = new Date(slot.end);
          const diff = (end.getTime() - start.getTime()) / 60000;
          if (Number.isFinite(diff) && diff > 0) {
            durationMinutes = Math.round(diff);
          }
        }
        if (durationMinutes === null || durationMinutes === 0) {
          durationMinutes = this.classData.durationMinutes ?? 0;
        }
        let startDisplay = slot.startLocalDisplay;
        const expectedAmount = this.normalizeAmount(this.selectedSlot?.expectedGrossAmount ?? this.classData?.price?.grossAmount);
        const actualAmount = this.normalizeAmount(result?.model?.totalWithDiscount);
        const priceChanged = expectedAmount !== null && actualAmount !== null
          ? Math.abs(actualAmount - expectedAmount) > 0.009
          : false;

        const actualPriceLabel = actualAmount !== null
          ? this.buildPriceLabel(actualAmount)
          : (result?.model?.totalWithDiscountStr ?? null);

        const fallbackPriceLabel = this.classData.price?.grossAmountStr
          || this.classData.priceLabel
          || (expectedAmount !== null ? this.buildPriceLabel(expectedAmount) : null);

        const priceLabel = actualPriceLabel || fallbackPriceLabel;
        const durationLabel = durationMinutes ? `${durationMinutes} min` : null;

        const normalizedTenantUrl = typeof this.classData?.tenantUrl === 'string'
          ? normalizeBaseUrl(this.classData.tenantUrl)
          : null;

        const cancelCustomerId = 0;
        const isOnlineValue = this.classData && this.classData.online === true
          ? true
          : (this.classData && this.classData.online === false ? false : null);

        const therapistSnapshot = this.classData?.therapist
          ? {
              ...this.classData.therapist,
              image: this.classData.therapist.avatarUrl || null,
              avatarUrl: this.classData.therapist.avatarUrl || null,
              specialty: this.classData.therapist.specialty || this.classData.therapist.title || null
            }
          : null;

        const saleModel = result?.model
          ? {
              ...result.model,
              tenantUrl: normalizedTenantUrl,
              tenantName: this.classData.tenantName ?? null,
              cancelUrl: null,
              cancelCustomerId,
              priceLabel,
              durationLabel,
            }
          : null;

        if (saleModel) {
          saleModel.therapist = therapistSnapshot;
          saleModel.specialistName = this.classData?.therapist?.name ?? null;
          saleModel.serviceName = this.classData?.description || this.classData?.title || '';
          saleModel.classTitle = this.classData?.description || this.classData?.title || '';
          saleModel.startDisplay = startDisplay;
          saleModel.durationLabel = durationLabel;
          saleModel.reservationType = 'class';
          if (isOnlineValue !== null) {
            saleModel.isOnline = isOnlineValue;
          }
        }

        if (saleModel?.bookingId && saleModel.cancelCustomerId !== null && normalizedTenantUrl) {
          saleModel.cancelUrl = `${normalizedTenantUrl}/api/publicbookings/cancel/${saleModel.bookingId}/${saleModel.cancelCustomerId}`;
        }

        if (saleModel && typeof saleModel.preBookingMinutes === 'number' && saleModel.preBookingMinutes > 0) {
          const expires = new Date();
          expires.setMinutes(expires.getMinutes() + saleModel.preBookingMinutes);
          saleModel.prebookExpiresAt = DateHelper.toSystemString(expires);
        }

        const checkoutContext = {
          type: 'class',
          tenantId: this.classData.tenantId,
          tenantName: this.classData.tenantName,
          tenantUrl: normalizedTenantUrl,
          classId: this.classData.classId,
          classTitle: this.classData.description || '',
          specialistName: this.classData.therapist?.name ?? null,
          therapist: therapistSnapshot,
          start: slot.start || this.classData.start,
          startDisplay,
          durationMinutes,
          durationLabel,
          priceLabel,
          isOnline: isOnlineValue,
          slot: slot
            ? {
                ...slot,
                resourceName: slot.resourceName || this.classData.therapist?.resourceName || null
              }
            : null,
          sale: saleModel,
          rawResponse: result
        };

        const bookingIdentifier = slot.bookingId ?? slot.id ?? this.classData.bookingId ?? null;
        if (bookingIdentifier) {
          checkoutContext.bookingId = bookingIdentifier;
        }

        if (priceChanged) {
          checkoutContext.priceUpdated = true;
          checkoutContext.previousPriceLabel = fallbackPriceLabel;
          checkoutContext.previousPriceAmount = expectedAmount;
          checkoutContext.actualPriceLabel = priceLabel;
          checkoutContext.actualPriceAmount = actualAmount;
        }

        getCheckoutStore().setCheckoutContext(checkoutContext);
        if (saleModel) {
          CartService.addSale(saleModel);
        }
        this.$emit('success', checkoutContext);
        this.closeErrorModal();

        router.push({ name: 'CheckoutPrebookSuccess' });
      } catch (error) {
        getCheckoutStore().setCheckoutContext(null);
        const errorPayload = this.buildErrorPayload(error);
        this.openErrorModal(errorPayload.details.length ? errorPayload.details : [errorPayload.message]);
        this.$emit('error', errorPayload);
      } finally {
        this.loading = false;
      }
    },
    openErrorModal(messages) {
      if (!this.showErrorModal) {
        return;
      }
      const sanitized = Array.isArray(messages)
        ? messages
            .map((msg) => (msg ? String(msg).trim() : ''))
            .filter((msg) => msg.length > 0)
        : [];
      this.errorModalMessages = sanitized.length ? [...new Set(sanitized)] : [this.resolvedFallbackMessage];
      this.errorModalVisible = true;
    },
    closeErrorModal() {
      if (!this.showErrorModal) {
        return;
      }
      this.errorModalVisible = false;
      this.errorModalMessages = [];
    }
  }
});
